import firebase from '../firebaseConfig';
import uuid from 'react-native-uuid';
import Track from './Track';
import Activity from './Activity';
import Artist from './Artist';

class Project {
    constructor(id, artistId, name, albumArtId, numberOfTracks, duration, artist) {
        this.id = id;
        this.artistId = artistId;
        this.name = name || "Untitled";
        this.albumArtId = albumArtId;
        this.numberOfTracks = numberOfTracks;
        this.duration = duration;
        this.artist = artist;
    }

    displayDetails() {
        console.log(`Title: ${this.name}`);
        console.log(`Album art id: ${this.albumArtId}`);
        console.log(`Number of Tracks: ${this.numberOfTracks}`);
    }

    getFirebaseRef() {
        return firebase.database().ref("artists").child(this.artistId)
            .child("projects").child(this.id);
    }

    // Backend `deleteProject` onDelete trigger handles archival, member
    // notifications, and share cleanup.
    delete() {
        return this.getFirebaseRef().remove();
    }

    _tracksFromSnapshot(snapshot) {
        const tracks = snapshot.val();
        if (!tracks) return [];
        return Object.entries(tracks).map(([key, value]) => {
            const metadata = value.metadata;
            const currentRevisionId = metadata["current revision"];
            const currentRevision = value.revisions[currentRevisionId].metadata;
            return new Track(
                key,
                currentRevisionId,
                metadata.name,
                currentRevision.duration,
                metadata["order weight"],
                metadata["locked"] === true ? true : false,
                this
            );
        }).sort((a, b) => a.orderWeight - b.orderWeight);
    }

    async getSnapshotOfTracks() {
        const snapshot = await this.getFirebaseRef().child("tracks").once("value");
        return this._tracksFromSnapshot(snapshot);
    }

    // Live tracks subscription — fires immediately and on every change,
    // returning an unsubscribe function. Lets background uploads (e.g. a
    // folder-drop import) surface without a manual page refresh.
    subscribeToTracks(onChange) {
        const ref = this.getFirebaseRef().child("tracks");
        const handler = ref.on(
            "value",
            (snapshot) => onChange(this._tracksFromSnapshot(snapshot)),
            () => onChange([]),
        );
        return () => ref.off("value", handler);
    }

    async getSnapshotOfActivity(Z = -1) {
        let query = this.getFirebaseRef().child("activity").orderByChild("time created");
    
        // If Z is not -1, limit the results to the last Z items
        if (Z !== -1) {
            query = query.limitToLast(Z);
        }
    
        const snapshot = await query.once("value");
        const activity = snapshot.val();
    
        const activityPromises = Object.keys(activity).map(async key => {
            const activityDetails = await Activity.getActivityById(key);
            activityDetails.project = this;
            return activityDetails;
        });
    
        const resolvedActivities = await Promise.all(activityPromises);
        return resolvedActivities.reverse();
    }

    static async create(artist, name) {
        const currentUser = firebase.auth().currentUser;
        if (!currentUser) throw new Error('Not signed in.');
        const trimmed = (name || '').trim() || 'Untitled';
        const projectId = uuid.v4();
        const now = Date.now();
        const metadata = {
            name: trimmed,
            "time created": now,
            "time modified": now,
            duration: 0,
            "track count": 0,
            "bytes stored": 0,
            "bytes downloaded": 0,
            "bytes uploaded": 0,
            uId: currentUser.uid,
        };
        await firebase.database()
            .ref("artists").child(artist.id)
            .child("projects").child(projectId)
            .child("metadata")
            .set(metadata);
        return new Project(projectId, artist.id, trimmed, null, 0, 0, artist);
    }

    static async getProjectById(artistId, projectId) {
        const dbRef = firebase.database().ref("artists").child(artistId)
            .child("projects").child(projectId);

        const deletedDbRef = firebase.app("deletedData").database().ref("artists")
            .child(artistId).child("projects").child(projectId);

        let snapshot = await dbRef.once("value");
        if (!snapshot.exists()) {
            snapshot = await deletedDbRef.once("value");
        }

        if (!snapshot.exists()) {
            // Project not found in either database
            throw new Error("Project not found");
        }

        const project = snapshot.val();
        const metadata = project.metadata;
        const albumArtId = metadata["album art"] || null;
        const artist = await Artist.getArtistById(artistId);

        return new Project(
            projectId,
            artist.id,
            metadata.name,
            albumArtId,
            metadata["track count"],
            metadata["duration"],
            artist
        );
    }
}

export default Project;